/**
 * ResetPassword page — set a new password using the reset token.
 * Re-skinned with consentry-v3 design system.
 */

import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setApiError('Invalid or missing password reset token.');
    }
  }, [token]);

  // Password requirements calculation (matching backend strict validation)
  const passwordCriteria = useMemo(() => {
    return {
      length: password.length >= 12,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
  }, [password]);

  const passwordScore = useMemo(() => {
    if (!password) return 0;
    const checks = Object.values(passwordCriteria);
    return checks.filter(Boolean).length;
  }, [passwordCriteria, password]);

  const isPasswordValid = passwordScore === 5;

  const validateForm = (): boolean => {
    let isValid = true;
    setPasswordError(null);
    setConfirmError(null);

    if (!password) {
      setPasswordError('New password is required');
      isValid = false;
    } else if (!isPasswordValid) {
      setPasswordError(
        'Password must be at least 12 characters and include uppercase, lowercase, number, and special character',
      );
      isValid = false;
    }

    if (!confirmPassword) {
      setConfirmError('Please confirm your new password');
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!token) {
      setApiError('Invalid or missing reset token.');
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await apiFetch('/v1/auth/reset-password', {
        method: 'POST',
        body: { token, password },
      });
      setSuccess(true);
      toast.success('Password reset successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const msg = getNetworkErrorMessage(err, 'Failed to reset password.');
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Reset Password" subtitle="Set a new password for your account">
      {success ? (
        <div className="space-y-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald/10 border border-emerald/20 flex items-center justify-center text-emerald text-xl font-bold">
            ✓
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-bold text-ink">Password Updated</h3>
            <p className="text-xs text-sub leading-relaxed">
              Your password has been changed successfully. Redirecting you to login...
            </p>
          </div>

          <Button
            type="button"
            variant="btn-p"
            fullWidth
            onClick={() => navigate('/login')}
            className="mt-4"
          >
            Sign In Now →
          </Button>
        </div>
      ) : apiError && !token ? (
        <div className="space-y-4 text-center">
          <div className="rounded-xl border border-err/20 bg-err/5 p-4 text-xs text-err font-medium">
            {apiError}
          </div>
          <Button
            type="button"
            variant="btn-p"
            fullWidth
            onClick={() => navigate('/forgot-password')}
          >
            Request New Reset Link
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Input
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
                if (apiError) setApiError(null);
              }}
              error={passwordError || undefined}
              disabled={isSubmitting}
              required
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-xs font-semibold text-sub hover:text-ink cursor-pointer focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              }
            />

            {/* Password Strength Meter & Checklist */}
            {password.length > 0 && (
              <div className="rounded-xl border border-line bg-surface p-3 space-y-2.5 text-xs">
                <div className="flex items-center justify-between font-medium">
                  <span className="text-sub">Password strength:</span>
                  <span
                    className={
                      passwordScore <= 2
                        ? 'text-err font-bold'
                        : passwordScore <= 4
                          ? 'text-warn font-bold'
                          : 'text-ok font-bold'
                    }
                  >
                    {passwordScore <= 2 ? 'Weak' : passwordScore <= 4 ? 'Fair' : 'Strong ✓'}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden flex">
                  <div
                    className={`h-full transition-all duration-300 ${
                      passwordScore <= 2
                        ? 'bg-err w-1/3'
                        : passwordScore <= 4
                          ? 'bg-warn w-2/3'
                          : 'bg-ok w-full'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-1 text-[11px]">
                  <div className={`flex items-center gap-1 ${passwordCriteria.length ? 'text-ok font-medium' : 'text-muted'}`}>
                    <span>{passwordCriteria.length ? '✓' : '○'}</span> 12+ characters
                  </div>
                  <div className={`flex items-center gap-1 ${passwordCriteria.upper ? 'text-ok font-medium' : 'text-muted'}`}>
                    <span>{passwordCriteria.upper ? '✓' : '○'}</span> Uppercase (A-Z)
                  </div>
                  <div className={`flex items-center gap-1 ${passwordCriteria.lower ? 'text-ok font-medium' : 'text-muted'}`}>
                    <span>{passwordCriteria.lower ? '✓' : '○'}</span> Lowercase (a-z)
                  </div>
                  <div className={`flex items-center gap-1 ${passwordCriteria.number ? 'text-ok font-medium' : 'text-muted'}`}>
                    <span>{passwordCriteria.number ? '✓' : '○'}</span> Number (0-9)
                  </div>
                  <div className={`flex items-center gap-1 col-span-2 ${passwordCriteria.special ? 'text-ok font-medium' : 'text-muted'}`}>
                    <span>{passwordCriteria.special ? '✓' : '○'}</span> Special character (!@#$%...)
                  </div>
                </div>
              </div>
            )}
          </div>

          <Input
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••••••"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmError) setConfirmError(null);
            }}
            error={confirmError || undefined}
            disabled={isSubmitting}
            required
          />

          {apiError && (
            <div className="rounded-xl border border-err/20 bg-err/5 p-3.5 text-xs text-err font-medium flex items-center gap-2.5">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{apiError}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="btn-p"
            fullWidth
            isLoading={isSubmitting}
            loadingText="Resetting password..."
            disabled={isSubmitting || !password || !confirmPassword}
            className="mt-2"
          >
            Reset Password &amp; Sign In
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export const ResetPassword = ResetPasswordPage;
